import { UUID_REGEX } from '@/constants';
import { isPersistedCitationId } from '@/ui/features/chat/utils/resolveChatCitations';
import type { Citation, ChatMessage } from '@/types/chat';

/**
 * One citation order for the whole chat (messages and drafts), so a number means the same source everywhere.
 */

type MessageContent = Pick<ChatMessage, 'content' | 'drafts'>;

/** `documentId` is null for history row citations; group those under one key. */
const documentKeyOf = (citation: Citation): string => citation.documentId ?? '';

/**
 * Citations with no position yet (their draft's text hasn't loaded). The map's key order isn't
 * stable, so sort by target: documents already numbered first, then page, extract and id.
 */
const compareUnplacedCitations =
  (chatCitations: Record<string, Citation>, documentRank: Map<string, number>) =>
  (leftId: string, rightId: string): number => {
    const left = chatCitations[leftId];
    const right = chatCitations[rightId];
    const rankOf = (citation: Citation) => documentRank.get(documentKeyOf(citation)) ?? Number.MAX_SAFE_INTEGER;

    return (
      rankOf(left) - rankOf(right) ||
      documentKeyOf(left).localeCompare(documentKeyOf(right)) ||
      (left.pageNumber ?? 0) - (right.pageNumber ?? 0) ||
      left.extract.localeCompare(right.extract) ||
      leftId.localeCompare(rightId)
    );
  };

/**
 * Every citation in the chat, numbered in reading order: each message's, then those of the drafts
 * shown under it (`draftCitationIds`, keyed by draftVersionId). Unreferenced non-uuid ids are
 * leftovers from streaming and are skipped.
 */
export const getChatCitationOrder = (
  messages: MessageContent[],
  chatCitations: Record<string, Citation>,
  draftCitationIds: Record<string, string[]> = {},
): string[] => {
  const citationIds = new Set(Object.keys(chatCitations));
  const cited: string[] = [];
  const seen = new Set<string>();

  const add = (id: string) => {
    if (citationIds.has(id) && !seen.has(id)) {
      seen.add(id);
      cited.push(id);
    }
  };

  messages.forEach((message) => {
    if (message.content) {
      for (const [, id] of message.content.matchAll(new RegExp(UUID_REGEX.source, UUID_REGEX.flags))) add(id);
    }
    message.drafts?.forEach((draft) => draftCitationIds[draft.draftVersionId]?.forEach(add));
  });

  const documentRank = new Map<string, number>();
  cited.forEach((id) => {
    const documentKey = documentKeyOf(chatCitations[id]);
    if (!documentRank.has(documentKey)) documentRank.set(documentKey, documentRank.size);
  });

  const unplaced = Array.from(citationIds)
    .filter((id) => !seen.has(id) && isPersistedCitationId(id))
    .sort(compareUnplacedCitations(chatCitations, documentRank));

  return [...cited, ...unplaced];
};

/** The drafts shown under the messages, top to bottom. */
export const getDraftVersionIds = (messages: Pick<ChatMessage, 'drafts'>[]): string[] =>
  messages.flatMap((message) => message.drafts?.map((draft) => draft.draftVersionId) ?? []);

/**
 * Citation map for a `GET /chat` response: the chat-level field, falling back to per-message
 * citations (same as `resolveChatCitations`).
 */
export const collectApiCitations = (
  chatMessages: { citations?: Record<string, Citation> }[],
  chatLevelCitations: Record<string, Citation> | undefined,
): Record<string, Citation> => {
  if (chatLevelCitations && Object.keys(chatLevelCitations).length > 0) return chatLevelCitations;

  const merged: Record<string, Citation> = {};
  chatMessages.forEach((message) => {
    Object.entries(message.citations ?? {}).forEach(([id, citation]) => {
      if (!merged[id]) merged[id] = citation;
    });
  });
  return merged;
};

/**
 * The Sources panel citation a draft pill points at. The pill's number is its index in the chat's
 * order; when that doesn't match the pill's document, fall back to the extract, then the document.
 */
export const findDraftCitationSourceId = (
  orderedIds: string[],
  chatCitations: Record<string, Citation>,
  target: { documentId: string; highlight: string },
  number: number | null,
): string | null => {
  const isSameDocument = (id: string) => chatCitations[id]?.documentId === target.documentId;

  const byNumber = number !== null ? orderedIds[number - 1] : undefined;
  if (byNumber && isSameDocument(byNumber)) return byNumber;

  const highlight = target.highlight.trim();
  const byExtract = highlight
    ? orderedIds.find((id) => isSameDocument(id) && chatCitations[id].extract.trim() === highlight)
    : undefined;

  return byExtract ?? orderedIds.find(isSameDocument) ?? null;
};
